import { useState, useSyncExternalStore, type ReactNode } from 'react'
import { Button } from './Button'
import { Dialog } from './Dialog'

type ConfirmOptions = {
  title: ReactNode
  /** Say exactly what will happen, with the concrete object ("Lena Wolf verliert den Zugang sofort."). */
  body?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void }

let pending: Pending | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

/**
 * Ask before something irreversible. Resolves `true` on confirm, `false` on cancel/Esc/backdrop.
 * Reversible actions do not confirm — run them and offer "Rückgängig" in a toast (DESIGN.md §5).
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  pending?.resolve(false)
  return new Promise((resolve) => {
    pending = { ...options, resolve }
    emit()
  })
}

function settle(ok: boolean) {
  pending?.resolve(ok)
  pending = null
  emit()
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** Mount once near the root. */
export function ConfirmHost() {
  const current = useSyncExternalStore(subscribe, () => pending, () => null)
  // Keep the last request rendered while it closes so the copy does not blank out mid-frame.
  const [shown, setShown] = useState<Pending | null>(null)
  if (current !== shown && current) setShown(current)
  const req = current ?? shown
  if (!req) return null
  return (
    <Dialog
      open={current !== null}
      onClose={() => settle(false)}
      title={req.title}
      description={req.body}
      size="sm"
      alert
      footer={
        <>
          {/* Initial focus on the safe choice for destructive confirms, so a stray Enter does no harm. */}
          <Button variant="secondary" onClick={() => settle(false)} data-autofocus={req.tone === 'danger' || undefined}>
            {req.cancelLabel ?? 'Abbrechen'}
          </Button>
          <Button variant={req.tone === 'danger' ? 'danger' : 'primary'} onClick={() => settle(true)} data-autofocus={req.tone !== 'danger' || undefined}>
            {req.confirmLabel}
          </Button>
        </>
      }
    />
  )
}
